import type { Component } from 'vue';
import { LUMI_ICONS } from './icons/vocabulary';

/** Canonical module registry — data-module keys, hues and glyphs for the whole suite. */
export type LumiModuleKey = 'suite' | 'ats' | 'time' | 'apprentice' | 'people' | 'admin';

// The registry carries NO displayed string. A module's name and its one-line launcher
// description are translated product copy, and they live exactly once, in
// the app-kit tier's shared nine-locale catalogue (`lumiModules.<key>.name` /
// `.description`), which every app merges underneath its own messages. That is the
// deliberate answer to the old objection that each `*-ui` would have to carry a
// translated one-liner for every OTHER module too: it does not, because the copy is
// shared once rather than duplicated per app. This tier stays headless — it takes
// labels by prop (ModuleSwitchTarget / ModuleBadge `label`) and never looks one up.
export interface LumiModule {
  key: LumiModuleKey;
  color: string;
  /** Glyph for the app-launcher grid (issue 195) and anywhere else a module needs a
   * compact icon. A component reference taken from the icon vocabulary (ADR 0096),
   * rendered by the consumer via `<component :is="icon" />`. */
  icon: Component;
}

// `color` resolves through the design tokens (tokens/colors.css is the SSOT for
// the module hues: --module-*, which follow the switcher's --nav-module-*); the
// suite default is the Tinte ink (--text-heading).
// Never hardcode the hex here — that would duplicate the SSOT and drift.
export const LUMI_MODULES: Record<LumiModuleKey, LumiModule> = {
  suite: {
    key: 'suite',
    color: 'var(--text-heading)',
    icon: LUMI_ICONS['module.suite'],
  },
  ats: {
    key: 'ats',
    color: 'var(--module-ats)',
    icon: LUMI_ICONS['module.recruiting'],
  },
  time: {
    key: 'time',
    color: 'var(--module-time)',
    icon: LUMI_ICONS['module.time'],
  },
  apprentice: {
    key: 'apprentice',
    color: 'var(--module-apprentice)',
    icon: LUMI_ICONS['module.apprentice'],
  },
  people: {
    key: 'people',
    color: 'var(--module-people)',
    icon: LUMI_ICONS['module.people'],
  },
  admin: {
    key: 'admin',
    color: 'var(--module-admin)',
    icon: LUMI_ICONS['module.admin'],
  },
};
